import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { merge } from 'lodash'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getAuthState, setAuthentication, setToken } from '../slice'
import { useGetUserRights } from './useGetUserRights'
import { useSetFirstLogin } from './useSetFirstLogin'

export const useAuth = () => {
  const { accounts, inProgress, instance } = useMsal()
  const isAuthenticated = useIsAuthenticated(accounts[0])

  const state = useAppSelector(getAuthState)
  const dispatch = useAppDispatch()
  const { fetchUserRights } = useGetUserRights()

  useSetFirstLogin()

  const isAuthenticatedState = state.isAuthenticated

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return
    if (isAuthenticatedState) return

    const tokenRequest = {
      account: accounts[0],
      scopes: [import.meta.env.VITE_SCOPES ?? ''],
    }

    if (!tokenRequest.account) {
      instance.acquireTokenRedirect(tokenRequest)
      return
    }

    instance
      .acquireTokenSilent(tokenRequest)
      .then(async (response) => {
        dispatch(setToken(response.accessToken))
        await fetchUserRights()
        dispatch(setAuthentication(true))
      })
      .catch(async (e) => {
        console.error('Token acquisition failed, redirecting...', e)
        await instance.acquireTokenRedirect(tokenRequest)
      })
  }, [
    inProgress,
    accounts,
    instance,
    dispatch,
    isAuthenticatedState,
    fetchUserRights,
  ])

  // Proactively refresh the token when the tab regains focus after being idle.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!accounts[0]) return

      instance
        .acquireTokenSilent({
          account: accounts[0],
          scopes: [import.meta.env.VITE_SCOPES ?? ''],
        })
        .then((response) => {
          dispatch(setToken(response.accessToken))
          dispatch(setAuthentication(true))
        })
        .catch(() => {
          // Silent refresh failed — the main effect will handle re-auth on the next API call.
        })
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [accounts, instance, dispatch])

  /**
   * Useful for downloading files or other cases where the result shouldn't be stored in memory.
   */
  const fetchWithAuth = (url: RequestInfo | URL, args?: RequestInit) => {
    const argsWithAuth = merge(args, {
      headers: { Authorization: `Bearer ${state.token}` },
    })

    return fetch(url, argsWithAuth)
  }

  return {
    token: state.token,
    instance,
    accounts,
    inProgress,
    state,
    fetchWithAuth,
    isAuthenticated,
  }
}
